import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { SITE_SETTINGS_DEFAULT, type SiteSettings } from "@tih/shared";
import { fetchSiteSettings } from "../api/client";

type SiteSettingsContextValue = {
  settings: SiteSettings;
  loading: boolean;
  refresh: () => Promise<void>;
  /**
   * Optimistically replace the cached settings (e.g. after the admin
   * launches the website from the dashboard) so the UI reflects the
   * change immediately without a round-trip.
   */
  setSettings: (next: SiteSettings) => void;
};

const SiteSettingsContext = createContext<SiteSettingsContextValue | undefined>(
  undefined,
);

export function SiteSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings>(SITE_SETTINGS_DEFAULT);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await fetchSiteSettings();
      setSettings(data);
    } catch {
      // Keep defaults — fail open (coming-soon) for the public site.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<SiteSettingsContextValue>(
    () => ({ settings, loading, refresh, setSettings }),
    [settings, loading, refresh],
  );

  return (
    <SiteSettingsContext.Provider value={value}>
      {children}
    </SiteSettingsContext.Provider>
  );
}

export function useSiteSettings(): SiteSettingsContextValue {
  const ctx = useContext(SiteSettingsContext);
  if (!ctx)
    throw new Error("useSiteSettings must be used inside <SiteSettingsProvider>");
  return ctx;
}
